// Core i18n factory. Keeps locale state + resolves keys against the
// provided namespace map. Missing keys:
//   - `throw` → Error
//   - `warn`  → console.warn + return raw key
//   - `silent`→ return raw key, no warning
//
// Key shape: '<namespace>.<path>...'. We split on the FIRST dot for the
// namespace, then look up the rest as a flat key inside that namespace
// (matches the catalog shape documented in §2).

import { interpolate } from './interpolate.js';
import { resolvePlural } from './plural.js';
import type {
  I18n,
  I18nConfig,
  InterpolationVars,
  Locale,
  NamespaceMap,
  OnMissing,
  TranslationValue,
} from './types.js';

function lookup(map: NamespaceMap, fullKey: string): TranslationValue | undefined {
  const dot = fullKey.indexOf('.');
  if (dot === -1) return undefined;
  const ns = fullKey.slice(0, dot);
  const rest = fullKey.slice(dot + 1);
  const bucket = map[ns];
  if (!bucket) return undefined;
  return bucket[rest];
}

function handleMissing(mode: OnMissing, key: string): string {
  if (mode === 'throw') {
    throw new Error(`[@govard/i18n-core] Missing translation key: "${key}"`);
  }
  if (mode === 'warn') {
    console.warn(`[@govard/i18n-core] Missing translation key: "${key}"`);
  }
  return key;
}

export function createI18n(config: I18nConfig): I18n {
  let locale: Locale = config.locale;
  const fallbackLocale = config.fallbackLocale ?? 'ru';
  const onMissing: OnMissing = config.onMissing ?? 'warn';
  const legacyNamespaces = config.namespaces;
  const catalogs = config.catalogs;
  const listeners = new Set<() => void>();

  if (!catalogs && !legacyNamespaces) {
    throw new Error('[@govard/i18n-core] createI18n requires `catalogs` or `namespaces`.');
  }

  function lookupForLocale(key: string): TranslationValue | undefined {
    if (!catalogs) return legacyNamespaces ? lookup(legacyNamespaces, key) : undefined;
    return lookup(catalogs[locale] ?? {}, key) ?? lookup(catalogs[fallbackLocale] ?? {}, key);
  }

  function resolveString(key: string, vars: InterpolationVars | undefined): string {
    const raw = lookupForLocale(key);
    if (raw === undefined) {
      return handleMissing(onMissing, key);
    }
    if (typeof raw !== 'string') {
      // Plural-shaped value accessed without tc(): warn and fall back to .other.
      console.warn(`[@govard/i18n-core] Key "${key}" is a plural entry — use tc() instead of t().`);
      return interpolate(raw.other, vars, key);
    }
    return interpolate(raw, vars, key);
  }

  function resolvePluralKey(
    key: string,
    count: number,
    vars: InterpolationVars | undefined,
  ): string {
    const raw = lookupForLocale(key);
    if (raw === undefined) {
      return handleMissing(onMissing, key);
    }
    if (typeof raw === 'string') {
      // Single-form translation used with tc — just interpolate count+vars.
      return interpolate(raw, { count, ...(vars ?? {}) }, key);
    }
    const picked = resolvePlural(raw, locale, count);
    return interpolate(picked, { count, ...(vars ?? {}) }, key);
  }

  return {
    get locale(): Locale {
      return locale;
    },
    t(key, vars) {
      return resolveString(key, vars);
    },
    tc(key, count, vars) {
      return resolvePluralKey(key, count, vars);
    },
    setLocale(next: Locale): void {
      if (locale === next) return;
      locale = next;
      for (const listener of listeners) listener();
    },
    subscribe(listener): () => void {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
